import React from "react";
import { motion } from "framer-motion";
import { Compass, ArrowRight, Sparkles } from "lucide-react";

const L = {
  en: {
    tag: "NEW · WESTERN AUSTRALIA",
    title: "Your copilot for landing in WA",
    sub: "Perth, Karratha, Broome — visa checklist, budget, best regions, emergency contacts. Everything you need in one 5-min guide.",
    cta: "Open the guide",
  },
  fr: {
    tag: "NOUVEAU · AUSTRALIE DE L'OUEST",
    title: "Ton copilote pour arriver en WA",
    sub: "Perth, Karratha, Broome — checklist visa, budget, meilleures régions, contacts d'urgence. Tout ce qu'il te faut dans un seul guide de 5 min.",
    cta: "Ouvrir le guide",
  },
  de: {
    tag: "NEU · WESTAUSTRALIEN",
    title: "Dein Copilot für die Ankunft in WA",
    sub: "Perth, Karratha, Broome — Visum-Checkliste, Budget, beste Regionen, Notfallkontakte. Alles in einem 5-Minuten-Guide.",
    cta: "Guide öffnen",
  },
  it: {
    tag: "NUOVO · AUSTRALIA OCCIDENTALE",
    title: "Il tuo copilota per arrivare in WA",
    sub: "Perth, Karratha, Broome — checklist visto, budget, migliori regioni, contatti di emergenza. Tutto in una guida di 5 min.",
    cta: "Apri la guida",
  },
  es: {
    tag: "NUEVO · AUSTRALIA OCCIDENTAL",
    title: "Tu copiloto para llegar a WA",
    sub: "Perth, Karratha, Broome — checklist de visado, presupuesto, mejores regiones, contactos de emergencia. Todo en una guía de 5 min.",
    cta: "Abrir la guía",
  },
  pt: {
    tag: "NOVO · AUSTRÁLIA OCIDENTAL",
    title: "O teu copiloto para chegar à WA",
    sub: "Perth, Karratha, Broome — checklist de visto, orçamento, melhores regiões, contactos de emergência. Tudo num guia de 5 min.",
    cta: "Abrir o guia",
  },
};

export function GuideWACard({ lang = "en", onOpen }) {
  const t = L[lang] || L.en;
  return (
    <section className="py-8 lg:py-14 bg-[#FAF6F0]" data-testid="guide-wa-card-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.button
          onClick={onOpen}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="group relative w-full text-left overflow-hidden rounded-3xl bg-gradient-to-br from-[#D95D39] via-[#C24F2E] to-[#9B3B1E] p-5 sm:p-8 lg:p-10 shadow-2xl hover:shadow-3xl transition-shadow"
          data-testid="guide-wa-card"
        >
          {/* Decorative overlay */}
          <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
            backgroundImage: "radial-gradient(circle at 15% 20%, rgba(255,255,255,0.5) 0%, transparent 45%), radial-gradient(circle at 85% 80%, rgba(255,255,255,0.3) 0%, transparent 45%)"
          }} />
          {/* Compass icon */}
          <div className="absolute -right-6 -bottom-6 sm:-right-8 sm:-bottom-8 opacity-15 group-hover:opacity-25 transition-opacity">
            <Compass className="w-40 h-40 sm:w-56 sm:h-56 text-white" strokeWidth={1} />
          </div>

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E5A93D] text-[#292524] text-[10px] sm:text-[11px] font-bold mb-3 whitespace-nowrap">
                <Sparkles className="w-3 h-3" /> {t.tag}
              </div>
              <h2 className="font-heading font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white leading-tight mb-2 sm:mb-3">
                {t.title}
              </h2>
              <p className="text-white/90 text-sm sm:text-base lg:text-lg leading-snug max-w-2xl">
                {t.sub}
              </p>
            </div>
            <div className="inline-flex items-center gap-2 bg-white text-[#292524] font-bold text-sm sm:text-base rounded-2xl px-5 sm:px-6 py-3 sm:py-4 flex-shrink-0 group-hover:bg-[#FAF6F0] transition-colors shadow-lg">
              <Compass className="w-4 h-4 sm:w-5 sm:h-5 text-[#D95D39]" />
              {t.cta}
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </motion.button>
      </div>
    </section>
  );
}
